import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, ArrowLeftIcon, WarningIcon, PencilSimpleIcon, TrashIcon, FileTextIcon, UploadSimpleIcon, EyeIcon, UsersThreeIcon, GitBranchIcon, LinkIcon, DownloadIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSort, SortHeader, productSummary, VarianceCell } from "@/lib/tableUtils";

function iso(d) { return d.toISOString().slice(0, 10); }
function subDays(d, n) { const x = new Date(d); x.setDate(x.getDate() - n); return x; }
function ddmmyyyy(isoStr) {
  if (!isoStr) return "—";
  const [y, m, d] = String(isoStr).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : isoStr;
}
const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const today = new Date();
  const [from, setFrom] = useState(iso(subDays(today, 29)));
  const [to, setTo] = useState(iso(today));
  const [types, setTypes] = useState([]);
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState(null); // delivery obj
  const [sortDir, setSortDir] = useState("desc");
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([
        api.get(`/customers/${id}/report?from_date=${from}&to_date=${to}`),
        api.get("/cylinder-types"),
      ]);
      setReport(r.data);
      setTypes(t.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, [id, from, to]);

  useEffect(() => { load(); }, [load]);

  if (!report) return <div className="text-slate-400 overline" data-testid="loading-customer">Loading…</div>;

  const { customer, totals, deliveries, outstanding } = report;
  const overLimit = customer.credit_limit > 0 && outstanding > customer.credit_limit;

  const sortedDeliveries = [...deliveries].sort((a, b) =>
    sortDir === "asc" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)
  );
  const visibleDeliveries = showAll ? sortedDeliveries : sortedDeliveries.slice(0, 10);

  return (
    <div className="space-y-10" data-testid="customer-detail-page">
      <div>
        <Link to="/customers" className="flex items-center text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900" data-testid="back-customers">
          <ArrowLeftIcon size={12} className="mr-1" /> All customers
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6 mt-4">
          <div>
            <div className="overline text-slate-500">{customer.category} · Ledger</div>
            <h1 className="font-display text-4xl font-black">{customer.name}</h1>
            <p className="text-slate-500 mt-2 font-mono-num text-sm">
              Rate {money(customer.rate)} · Discount {money(customer.discount)}
              {customer.credit_limit > 0 && <> · Credit Limit {money(customer.credit_limit)}</>}
            </p>
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div><Label className="overline">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="cust-from" /></div>
            <div><Label className="overline">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="cust-to" /></div>
            {isOwner && <EditCustomerDialog customer={customer} types={types} onDone={load} />}
            <CustomerDocumentsButton customerId={id} canWrite={isOwner || user?.role !== "delivery_entry"} isOwner={isOwner} />
            <AddSubBranchDialog parent={customer} onDone={load} />
            <AddDeliveryDialog customer={customer} types={types} onDone={load} />
          </div>
        </div>
      </div>

      {overLimit && (
        <div className="border border-[#FF2400] bg-[#FF2400]/5 px-4 py-3 text-sm flex items-center gap-3" data-testid="over-limit-banner">
          <WarningIcon size={16} weight="bold" className="text-[#FF2400]" />
          <span className="text-slate-800">
            Outstanding <span className="font-mono-num font-bold text-[#FF2400]">{money(outstanding)}</span> exceeds credit limit {money(customer.credit_limit)}.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
        <Kpi label="Filled Given" value={totals.filled_given} testid="k-filled" />
        <Kpi label="Empty Received" value={totals.empty_received} testid="k-empty" />
        <Kpi label="Billed" value={money(totals.amount)} testid="k-billed" />
        <Kpi label="Outstanding (all-time)" value={money(outstanding)} accent testid="k-outstanding" />
      </div>

      {/* Per-cylinder-type bifurcation (only shown if customer uses >1 product) */}
      <PerProductBreakdown deliveries={deliveries} />

      {/* Group Ledger (only for parent customers with children) */}
      <GroupLedger customerId={id} from={from} to={to} />

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
          <div className="overline text-brand-blue">Ledger · {sortedDeliveries.length} entries</div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSortDir((s) => (s === "asc" ? "desc" : "asc"))}
              className="text-xs uppercase tracking-wider text-brand-blue hover:text-brand-orange-ink"
              data-testid="ledger-sort-toggle"
            >
              Date {sortDir === "asc" ? "↑ ASC" : "↓ DESC"}
            </button>
            {sortedDeliveries.length > 10 && (
              <button
                onClick={() => setShowAll((v) => !v)}
                className="text-xs uppercase tracking-wider text-brand-orange-ink hover:text-brand-blue"
                data-testid="ledger-expand-toggle"
              >
                {showAll ? "Show first 10" : `Show all ${sortedDeliveries.length}`}
              </button>
            )}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider">Date</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Cylinder</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Filled</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Empty</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Net Rate</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Amount</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Paid</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Credit</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Note</TableHead>
              {isOwner && <TableHead className="text-right uppercase text-xs tracking-wider w-24">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleDeliveries.map((d) => {
              const emptyShort = (d.filled_given || 0) > 0 && (d.empty_received || 0) < (d.filled_given || 0);
              return (
              <TableRow
                key={d.id}
                className={`border-b border-slate-100 ${emptyShort ? "bg-red-50/70 hover:bg-red-100/70" : "hover:bg-slate-50"}`}
                data-testid={`delivery-row-${d.id}`}
              >
                <TableCell className="font-mono-num text-slate-600 text-sm">{ddmmyyyy(d.date)}</TableCell>
                <TableCell className="font-semibold">{d.cylinder_name}</TableCell>
                <TableCell className="text-right font-mono-num">{d.filled_given}</TableCell>
                <TableCell className={`text-right font-mono-num ${emptyShort ? "text-[#FF2400] font-bold" : ""}`}>
                  {d.empty_received}
                  {emptyShort && <span className="ml-1 text-[10px] font-semibold">−{(d.filled_given || 0) - (d.empty_received || 0)}</span>}
                </TableCell>
                <TableCell className="text-right font-mono-num">{money(d.net_rate)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{money(d.amount)}</TableCell>
                <TableCell className="text-slate-600 text-sm">
                  <span className="font-mono-num">{money(d.payment_amount)}</span>
                  {d.payment_mode && <span className="overline ml-2" style={{ fontSize: 12 }}>{d.payment_mode === "wallet" ? "IOC Paid" : d.payment_mode}</span>}
                </TableCell>
                <TableCell className="text-right font-mono-num">
                  <span className={d.credit > 0 ? "text-[#FF2400]" : "text-slate-400"}>{money(d.credit)}</span>
                </TableCell>
                <TableCell className="text-slate-500 text-sm">{d.note || "—"}</TableCell>
                {isOwner && (
                  <TableCell className="text-right space-x-1">
                    <button
                      onClick={() => setEditing(d)}
                      className="text-slate-500 hover:text-brand-blue p-1"
                      title="Edit"
                      data-testid={`delivery-edit-${d.id}`}
                    ><PencilSimpleIcon size={14} /></button>
                    <button
                      onClick={async () => {
                        if (!confirm(`Delete this delivery on ${ddmmyyyy(d.date)}? Stock and payment will be rolled back.`)) return;
                        try {
                          await api.delete(`/customer-deliveries/${d.id}`);
                          toast.success("Delivery deleted");
                          load();
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                      className="text-slate-400 hover:text-[#FF2400] p-1"
                      title="Delete"
                      data-testid={`delivery-delete-${d.id}`}
                    ><TrashIcon size={14} /></button>
                  </TableCell>
                )}
              </TableRow>
              );
            })}
            {sortedDeliveries.length === 0 && (
              <TableRow><TableCell colSpan={isOwner ? 10 : 9} className="text-center text-slate-400 py-10">No deliveries in this range.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {editing && isOwner && (
        <EditDeliveryDialog
          delivery={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PerProductBreakdown({ deliveries }) {
  const byProduct = deliveries.reduce((acc, d) => {
    const key = d.cylinder_name || d.cylinder_type_id;
    if (!acc[key]) acc[key] = { name: key, filled: 0, empty: 0, amount: 0, count: 0 };
    acc[key].filled += d.filled_given || 0;
    acc[key].empty += d.empty_received || 0;
    acc[key].amount += d.amount || 0;
    acc[key].count += 1;
    return acc;
  }, {});
  const rows = Object.values(byProduct);
  if (rows.length <= 1) return null;

  return (
    <div className="border-2 border-brand-blue bg-white p-4" data-testid="per-product-breakdown">
      <div className="overline text-brand-orange-ink mb-2">Consumption breakdown · by product</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <div key={r.name} className="border border-slate-200 p-3" data-testid={`bd-${r.name.replace(/\W+/g, "-")}`}>
            <div className="font-display font-black text-brand-blue text-lg">{r.name}</div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div>
                <div className="overline text-slate-500" style={{ fontSize: 10 }}>Filled</div>
                <div className="font-mono-num text-xl font-bold text-brand-blue">{r.filled}</div>
              </div>
              <div>
                <div className="overline text-slate-500" style={{ fontSize: 10 }}>Empty</div>
                <div className="font-mono-num text-xl font-bold">{r.empty}</div>
              </div>
              <div>
                <div className="overline text-slate-500" style={{ fontSize: 10 }}>Billed</div>
                <div className="font-mono-num text-sm font-bold">₹{r.amount.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditDeliveryDialog({ delivery, onClose, onSaved }) {
  const [filled, setFilled] = useState(String(delivery.filled_given ?? 0));
  const [empty, setEmpty] = useState(String(delivery.empty_received ?? 0));
  const [rate, setRate] = useState(String(delivery.rate ?? 0));
  const [discount, setDiscount] = useState(String(delivery.discount ?? 0));
  const [payAmount, setPayAmount] = useState(String(delivery.payment_amount ?? 0));
  const [payMode, setPayMode] = useState(delivery.payment_mode || "cash");
  const [note, setNote] = useState(delivery.note || "");
  const [d, setD] = useState(delivery.date);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/customer-deliveries/${delivery.id}`, {
        filled_given: parseInt(filled) || 0,
        empty_received: parseInt(empty) || 0,
        rate: parseFloat(rate) || 0,
        discount: parseFloat(discount) || 0,
        payment_amount: parseFloat(payAmount) || 0,
        payment_mode: (parseFloat(payAmount) || 0) > 0 ? payMode : "",
        note, date: d,
      });
      toast.success("Delivery updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="edit-delivery-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit delivery · {delivery.cylinder_name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="edit-date" /></div>
            <div><Label className="overline">Rate ₹/cyl</Label>
              <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="edit-rate" /></div>
            <div><Label className="overline">Filled given</Label>
              <Input type="number" min="0" value={filled} onChange={(e) => setFilled(e.target.value)} className="rounded-none font-mono-num" data-testid="edit-filled" /></div>
            <div><Label className="overline">Empty received</Label>
              <Input type="number" min="0" value={empty} onChange={(e) => setEmpty(e.target.value)} className="rounded-none font-mono-num" data-testid="edit-empty" /></div>
            <div><Label className="overline">Discount ₹/cyl</Label>
              <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="rounded-none font-mono-num" data-testid="edit-discount" /></div>
            <div><Label className="overline">Payment ₹</Label>
              <Input type="number" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="rounded-none font-mono-num" data-testid="edit-payment" /></div>
            <div className="col-span-2"><Label className="overline">Payment mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="rounded-none" data-testid="edit-paymode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gpay">Google Pay</SelectItem>
                  <SelectItem value="wallet">IOC Paid</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="overline">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="edit-note" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-brand-blue text-white" data-testid="edit-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className={`p-6 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-slate-900 text-white" : ""}`} data-testid={testid}>
      <div className={`overline ${accent ? "text-white/60" : "text-slate-500"}`}>{label}</div>
      <div className="font-mono-num text-2xl font-bold mt-3">{value}</div>
    </div>
  );
}

function AddDeliveryDialog({ customer, types, onDone }) {
  const [open, setOpen] = useState(false);
  const LS_KEY = `customer-${customer.id}-default-cyl`;
  const [typeId, setTypeId] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [filled, setFilled] = useState("");
  const [empty, setEmpty] = useState("");
  const [rate, setRate] = useState("");
  const [discount, setDiscount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payAmount, setPayAmount] = useState("0");
  const [note, setNote] = useState("");
  const [d, setD] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!typeId && types[0]) setTypeId(types[0].id);
  }, [types, typeId]);

  useEffect(() => {
    if (typeId) localStorage.setItem(LS_KEY, typeId);
  }, [typeId, LS_KEY]);

  useEffect(() => {
    if (open) {
      setRate(customer.rate?.toString() || "");
      setDiscount(customer.discount?.toString() || "");
    }
  }, [open, customer]);

  const effectiveRate = Math.max((parseFloat(rate || 0) - parseFloat(discount || 0)) || 0, 0);
  const preview = (effectiveRate * parseInt(filled || 0, 10)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    if (!typeId) return toast.error("Select a cylinder type");

    // Duplicate detection: any existing delivery for the same customer + cyl + date?
    try {
      const dup = await api.get("/customer-deliveries", {
        params: { customer_id: customer.id, from_date: d, to_date: d },
      });
      const match = (dup.data || []).find(
        (x) => x.cylinder_type_id === typeId && x.filled_given === (parseInt(filled || 0, 10) || 0)
      );
      if (match) {
        const proceed = confirm(
          `⚠️ Possible duplicate!\n\n` +
          `A delivery for the same customer on ${d}, same cylinder, same filled qty (${match.filled_given}) already exists.\n\n` +
          `Save this new entry anyway?`
        );
        if (!proceed) return;
      }
    } catch { /* non-blocking */ }

    try {
      await api.post("/customer-deliveries", {
        customer_id: customer.id,
        cylinder_type_id: typeId,
        filled_given: parseInt(filled || 0, 10),
        empty_received: parseInt(empty || 0, 10),
        rate: rate === "" ? null : parseFloat(rate),
        discount: discount === "" ? null : parseFloat(discount),
        payment_mode: payAmount > 0 ? payMode : "",
        payment_amount: parseFloat(payAmount || 0),
        note, date: d,
      });
      toast.success("Delivery recorded");
      setOpen(false); setFilled(""); setEmpty(""); setPayAmount("0"); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-slate-900 hover:bg-black text-white" data-testid="add-delivery-button">
          <PlusIcon size={16} className="mr-2" /> New Delivery
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Delivery · {customer.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-delivery-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Cylinder</Label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger className="rounded-none" data-testid="delivery-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="delivery-date-input" />
            </div>
            <div>
              <Label className="overline">Filled given</Label>
              <Input type="number" min="0" value={filled} onChange={(e) => setFilled(e.target.value)} required className="rounded-none font-mono-num" data-testid="delivery-filled-input" />
            </div>
            <div>
              <Label className="overline">Empty received</Label>
              <Input type="number" min="0" value={empty} onChange={(e) => setEmpty(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-empty-input" />
            </div>
            <div>
              <Label className="overline">Rate ₹/cyl</Label>
              <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-rate-input" />
            </div>
            <div>
              <Label className="overline">Discount ₹/cyl</Label>
              <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-discount-input" />
            </div>
          </div>
          <div className="border border-slate-200 p-3 flex items-center justify-between text-sm">
            <span className="overline text-slate-500">Bill preview</span>
            <span className="font-mono-num font-bold">₹ {preview}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Payment mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="rounded-none" data-testid="delivery-pay-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gpay">Google Pay</SelectItem>
                  <SelectItem value="wallet">IOC Paid</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Payment amount</Label>
              <Input type="number" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-pay-amount-input" />
            </div>
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="delivery-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="delivery-submit-button">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════ CUSTOMER DOCUMENTS ═══════════════════════════ */
function CustomerDocumentsButton({ customerId, canWrite, isOwner }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);

  // Peek doc count so the pill badge shows even when the modal is closed.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/customers/${customerId}/documents`);
        if (alive) setCount((r.data || []).length);
      } catch { if (alive) setCount(0); }
    })();
  }, [customerId, open]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-none border-brand-blue text-brand-blue hover:bg-brand-blue-tint h-10"
        data-testid="open-documents-btn"
        title="KYC / SV / MOU documents"
      >
        <FileTextIcon size={14} className="mr-2" /> Documents
        {count > 0 && (
          <span
            className="ml-2 px-1.5 py-0 bg-brand-orange-tint text-brand-orange-ink font-mono-num font-bold"
            style={{ fontSize: 10, minWidth: 18, textAlign: "center" }}
            data-testid="documents-count-badge"
          >
            {count}
          </span>
        )}
      </Button>
      {open && (
        <CustomerDocumentsDialog
          customerId={customerId}
          canWrite={canWrite}
          isOwner={isOwner}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CustomerDocumentsDialog({ customerId, canWrite, isOwner, onClose }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("KYC");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customers/${customerId}/documents`);
      setDocs(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load documents");
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file first");
    if (file.size > 10 * 1024 * 1024) return toast.error("File is larger than 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      fd.append("label", label);
      await api.post(`/customers/${customerId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setFile(null); setLabel("");
      const el = document.getElementById(`doc-file-${customerId}`);
      if (el) el.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const viewDoc = async (d) => {
    try {
      const r = await api.get(`/customer-documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to open");
    }
  };

  const downloadDoc = async (d) => {
    setDownloading(d.id);
    try {
      const r = await api.get(`/customer-documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || `document-${d.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success(`Downloaded ${d.original_filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to download");
    } finally { setDownloading(null); }
  };

  const del = async (d) => {
    if (!confirm(`Delete "${d.original_filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/customer-documents/${d.id}`);
      toast.success("Document deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const humanSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const typeBadgeStyle = (t) => {
    const map = {
      KYC:   { bg: "#EFF6FF", fg: "#1E3A8A" },
      SV:    { bg: "#FFF4EA", fg: "#B45309" },
      MOU:   { bg: "#F0FDF4", fg: "#166534" },
      Other: { bg: "#F1F5F9", fg: "#334155" },
    };
    const c = map[t] || map.Other;
    return { background: c.bg, color: c.fg };
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-slate-300 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="customer-documents-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-blue flex items-center gap-2">
            <FileTextIcon size={20} className="text-brand-blue" />
            Customer Documents
            <span className="overline text-slate-400 ml-2 text-xs">KYC · SV · MOU · Other</span>
          </DialogTitle>
        </DialogHeader>

        {canWrite && (
          <form onSubmit={submit} className="border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="doc-upload-form">
            <div>
              <Label className="overline">Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-none" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="KYC">KYC</SelectItem>
                  <SelectItem value="SV">SV</SelectItem>
                  <SelectItem value="MOU">MOU</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Label (optional)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Aadhaar front" className="rounded-none" data-testid="doc-label-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="overline">File · PDF/PNG/JPG · max 10 MB</Label>
              <input
                id={`doc-file-${customerId}`}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full border border-slate-300 rounded-none px-3 py-2 text-sm bg-white file:mr-3 file:py-1 file:px-2 file:border-0 file:bg-slate-900 file:text-white file:uppercase file:text-xs"
                data-testid="doc-file-input"
              />
            </div>
            <div className="md:col-span-4">
              <Button type="submit" disabled={uploading || !file} className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="doc-upload-btn">
                <UploadSimpleIcon size={14} className="mr-2" />
                {uploading ? "Uploading…" : "Upload document"}
              </Button>
            </div>
          </form>
        )}

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider">Type</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">File</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Label</TableHead>
                <TableHead className="uppercase text-xs tracking-wider">Uploaded</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Size</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider w-40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">Loading…</TableCell></TableRow>}
              {!loading && docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                    No documents yet. Upload KYC, SV or MOU documents above.
                  </TableCell>
                </TableRow>
              )}
              {docs.map((d) => (
                <TableRow key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                  <TableCell>
                    <span className="px-2 py-0.5 text-xs font-semibold uppercase tracking-wider" style={typeBadgeStyle(d.doc_type)}>
                      {d.doc_type || "Other"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono-num text-xs">{d.original_filename}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{d.label || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-xs">
                    <div>{ddmmyyyy(d.created_at)}</div>
                    {d.uploaded_by_name && <div className="text-slate-400">by {d.uploaded_by_name}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500 text-xs">{humanSize(d.size)}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    <button
                      onClick={() => viewDoc(d)}
                      className="text-brand-blue hover:text-brand-orange-ink p-1"
                      title="View in new tab"
                      data-testid={`doc-view-${d.id}`}
                    ><EyeIcon size={16} /></button>
                    <button
                      onClick={() => downloadDoc(d)}
                      disabled={downloading === d.id}
                      className="text-emerald-700 hover:text-emerald-900 p-1 disabled:opacity-40"
                      title="Download to device"
                      data-testid={`doc-download-${d.id}`}
                    ><DownloadIcon size={16} /></button>
                    {isOwner && (
                      <button
                        onClick={() => del(d)}
                        className="text-slate-400 hover:text-[#FF2400] p-1"
                        title="Delete"
                        data-testid={`doc-delete-${d.id}`}
                      ><TrashIcon size={16} /></button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="documents-dialog-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════ GROUP LEDGER ═══════════════════════════ */
function GroupLedger({ customerId, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/customers/${customerId}/group-report`, { params: { from_date: from, to_date: to } });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [customerId, from, to]);

  // Hide entirely if this customer isn't a group (only itself in the rollup)
  const { sorted: sortedGroupRows, sortKey: glSortKey, sortDir: glSortDir, toggleSort: glToggleSort } =
    useSort(data?.rows || [], "name", "asc");

  if (loading) return null;
  if (!data || !data.rows || data.rows.length < 2) return null;

  const { totals, root } = data;
  const totalVariance = (totals.filled || 0) - (totals.empty || 0);

  return (
    <section className="pt-4 border-t border-slate-200 space-y-3" data-testid="group-ledger-section">
      <div className="flex items-center gap-2">
        <UsersThreeIcon size={18} className="text-brand-orange-ink" />
        <h2 className="font-display text-2xl font-black text-brand-blue">Group Rollup</h2>
        <span className="overline text-slate-400 ml-2">{root?.name} + {data.rows.length - 1} outlet{data.rows.length - 1 > 1 ? "s" : ""}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-slate-200 bg-white">
        <Kpi label="Filled" value={totals.filled} testid="gl-filled" />
        <Kpi label="Empty" value={totals.empty} testid="gl-empty" />
        <Kpi label="Variance" value={<VarianceCell value={totalVariance} testid="gl-variance-kpi" />} testid="gl-variance" />
        <Kpi label="Billed" value={money(totals.amount)} testid="gl-billed" />
        <Kpi label="Paid" value={money(totals.paid)} testid="gl-paid" />
        <Kpi label="Outstanding (lifetime)" value={money(totals.outstanding_lifetime)} accent testid="gl-outstanding" />
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <TableHead><SortHeader label="Outlet" sortKey="name" active={glSortKey==="name"} dir={glSortDir} onClick={glToggleSort} testid="gl-sort-name" /></TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-slate-500">Products (F / E)</TableHead>
              <TableHead className="text-right"><SortHeader label="Filled" sortKey="filled" active={glSortKey==="filled"} dir={glSortDir} onClick={glToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Empty" sortKey="empty" active={glSortKey==="empty"} dir={glSortDir} onClick={glToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Variance" sortKey="variance" active={glSortKey==="variance"} dir={glSortDir} onClick={glToggleSort} align="right" testid="gl-sort-variance" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Amount" sortKey="amount" active={glSortKey==="amount"} dir={glSortDir} onClick={glToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Paid" sortKey="paid" active={glSortKey==="paid"} dir={glSortDir} onClick={glToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Outstanding" sortKey="outstanding_lifetime" active={glSortKey==="outstanding_lifetime"} dir={glSortDir} onClick={glToggleSort} align="right" /></TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedGroupRows.map((r) => (
              <TableRow key={r.customer_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`gl-row-${r.customer_id}`}>
                <TableCell className="font-semibold">
                  <Link to={`/customers/${r.customer_id}`} className="hover:text-brand-orange-ink">
                    {r.name} {r.customer_id === root?.id && <span className="overline text-brand-orange-ink ml-1">(Parent)</span>}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-slate-600 font-mono-num max-w-[240px]" title={productSummary(r.products)}>
                  {productSummary(r.products)}
                </TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{r.filled}</TableCell>
                <TableCell className="text-right font-mono-num">{r.empty}</TableCell>
                <TableCell className="text-right"><VarianceCell value={r.variance ?? ((r.filled || 0) - (r.empty || 0))} testid={`gl-variance-${r.customer_id}`} /></TableCell>
                <TableCell className="text-right font-mono-num">{money(r.amount)}</TableCell>
                <TableCell className="text-right font-mono-num">{money(r.paid)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">
                  <span className={r.outstanding_lifetime > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(r.outstanding_lifetime)}</span>
                </TableCell>
                <TableCell>
                  <Link to={`/customers/${r.customer_id}`} className="text-xs uppercase tracking-wider text-brand-blue hover:text-brand-orange-ink" data-testid={`gl-open-${r.customer_id}`}>
                    Open
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

/* ═══════════════════════════ ADD SUB-BRANCH DIALOG ═══════════════════════════ */
function AddSubBranchDialog({ parent, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new"); // "new" | "attach"
  const [saving, setSaving] = useState(false);

  // "New" mode fields
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [rate, setRate] = useState(String(parent?.rate ?? 0));
  const [discount, setDiscount] = useState(String(parent?.discount ?? 0));

  // "Attach" mode fields
  const [attachId, setAttachId] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open || mode !== "attach") return;
    (async () => {
      try {
        const r = await api.get("/customers");
        // Only commercial standalone customers (no parent yet, not the parent itself, not already a child of it)
        const list = (r.data || []).filter(
          (c) => c.id !== parent.id && !c.parent_customer_id && (c.category || "commercial") === "commercial"
        );
        setCandidates(list);
      } catch { setCandidates([]); }
    })();
  }, [open, mode, parent.id]);

  const reset = () => {
    setName(""); setPhone(""); setAddress("");
    setRate(String(parent?.rate ?? 0)); setDiscount(String(parent?.discount ?? 0));
    setAttachId(""); setQ(""); setMode("new");
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/customers", {
        name, phone, address,
        category: "commercial",
        rate: parseFloat(rate) || 0,
        discount: parseFloat(discount) || 0,
        credit_limit: parent?.credit_limit || 0,
        opening_balance: 0,
        parent_customer_id: parent.id,
      });
      toast.success(`Sub-branch "${name}" attached to ${parent.name}`);
      setOpen(false); reset(); onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const submitAttach = async (e) => {
    e.preventDefault();
    if (!attachId) return toast.error("Pick a customer to attach");
    setSaving(true);
    try {
      await api.patch(`/customers/${attachId}`, { parent_customer_id: parent.id });
      const picked = candidates.find((c) => c.id === attachId);
      toast.success(`Attached "${picked?.name}" as sub-branch of ${parent.name}`);
      setOpen(false); reset(); onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const filtered = candidates.filter((c) =>
    q.trim() === "" || (c.name || "").toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-brand-orange text-brand-orange-ink hover:bg-brand-orange-tint h-10" data-testid="add-sub-branch-btn">
          <GitBranchIcon size={14} className="mr-2" /> Sub-branch
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Add sub-branch to {parent.name}</DialogTitle>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="flex border border-slate-200" data-testid="sub-branch-mode-tabs">
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 py-2 text-xs uppercase tracking-wider ${mode === "new" ? "bg-brand-blue text-white" : "bg-white text-slate-500 hover:text-brand-blue"}`}
            data-testid="sub-branch-mode-new"
          >
            <PlusIcon size={12} className="inline mr-1" /> Create new
          </button>
          <button
            type="button"
            onClick={() => setMode("attach")}
            className={`flex-1 py-2 text-xs uppercase tracking-wider ${mode === "attach" ? "bg-brand-blue text-white" : "bg-white text-slate-500 hover:text-brand-blue"}`}
            data-testid="sub-branch-mode-attach"
          >
            <LinkIcon size={12} className="inline mr-1" /> Attach existing
          </button>
        </div>

        {mode === "new" ? (
          <form onSubmit={submitNew} className="space-y-4" data-testid="sub-branch-new-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="overline">Sub-branch name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder={`e.g. ${parent.name} – Branch B`} className="rounded-none" data-testid="sub-branch-name-input" />
              </div>
              <div>
                <Label className="overline">Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none font-mono-num" data-testid="sub-branch-phone-input" />
              </div>
              <div>
                <Label className="overline">Rate (₹/cyl)</Label>
                <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="sub-branch-rate-input" />
              </div>
              <div className="col-span-2">
                <Label className="overline">Address</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-none" data-testid="sub-branch-address-input" />
              </div>
              <div>
                <Label className="overline">Discount (₹/cyl)</Label>
                <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="rounded-none font-mono-num" data-testid="sub-branch-discount-input" />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Rate &amp; discount default to the parent's values — override for this branch if the deal is different.
            </p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="sub-branch-create-btn">
                {saving ? "Adding…" : "Create sub-branch"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={submitAttach} className="space-y-4" data-testid="sub-branch-attach-form">
            <div>
              <Label className="overline">Search standalone customers</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type name…" className="rounded-none" data-testid="sub-branch-search-input" />
              <p className="text-xs text-slate-400 mt-1">
                Only standalone commercial customers (not already clubbed) are shown.
              </p>
            </div>
            <div className="border border-slate-200 max-h-72 overflow-y-auto divide-y divide-slate-100">
              {filtered.length === 0 && (
                <div className="p-6 text-center text-slate-400 text-sm">
                  {candidates.length === 0
                    ? "No standalone customers available to attach."
                    : "No matches. Refine the search or Create new."}
                </div>
              )}
              {filtered.map((c) => (
                <label key={c.id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 ${attachId === c.id ? "bg-brand-blue-tint" : ""}`} data-testid={`sub-branch-candidate-${c.id}`}>
                  <input
                    type="radio"
                    name="attachId"
                    value={c.id}
                    checked={attachId === c.id}
                    onChange={() => setAttachId(c.id)}
                    className="accent-slate-900"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-brand-blue">{c.name}</div>
                    <div className="text-xs text-slate-400 font-mono-num">
                      {c.phone || "—"} · rate {money(c.rate)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={saving || !attachId} className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="sub-branch-attach-btn">
                {saving ? "Attaching…" : "Attach as sub-branch"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════ EDIT CUSTOMER DIALOG ═══════════════════════════ */
function EditCustomerDialog({ customer, types, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(customer);
  useEffect(() => { setForm(customer); }, [customer, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/customers/${customer.id}`, {
        name: form.name, phone: form.phone, address: form.address,
        category: form.category,
        rate: parseFloat(form.rate) || 0,
        discount: parseFloat(form.discount) || 0,
        credit_limit: parseFloat(form.credit_limit) || 0,
        default_cylinder_type_id: form.default_cylinder_type_id || null,
        note: form.note || "",
      });
      toast.success("Customer updated");
      setOpen(false); onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-slate-900 h-10" data-testid="edit-customer-btn">
          <PencilSimpleIcon size={14} className="mr-2" /> Edit profile
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Edit customer profile</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-customer-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="overline">Name</Label>
              <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} required className="rounded-none" data-testid="edit-name" />
            </div>
            <div>
              <Label className="overline">Phone</Label>
              <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-none font-mono-num" data-testid="edit-phone" />
            </div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={form.category || "commercial"} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="rounded-none" data-testid="edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Address</Label>
              <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-none" data-testid="edit-address" />
            </div>
            <div>
              <Label className="overline">Rate (₹/cyl)</Label>
              <Input type="number" step="0.01" value={form.rate ?? 0} onChange={(e) => set("rate", e.target.value)} className="rounded-none font-mono-num" data-testid="edit-rate" />
            </div>
            <div>
              <Label className="overline">Discount (₹/cyl)</Label>
              <Input type="number" step="0.01" value={form.discount ?? 0} onChange={(e) => set("discount", e.target.value)} className="rounded-none font-mono-num" data-testid="edit-discount" />
            </div>
            <div>
              <Label className="overline">Credit Limit (₹)</Label>
              <Input type="number" step="0.01" value={form.credit_limit ?? 0} onChange={(e) => set("credit_limit", e.target.value)} className="rounded-none font-mono-num" data-testid="edit-credit-limit" />
            </div>
            <div>
              <Label className="overline">Default cylinder</Label>
              <Select value={form.default_cylinder_type_id || "__none__"} onValueChange={(v) => set("default_cylinder_type_id", v === "__none__" ? null : v)}>
                <SelectTrigger className="rounded-none" data-testid="edit-default-cylinder"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {(types || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Note (optional)</Label>
              <Input value={form.note || ""} onChange={(e) => set("note", e.target.value)} className="rounded-none" data-testid="edit-note" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="edit-customer-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

